/**
 * Entity Type to App Type Mapping
 * 
 * Centralized logic for determining which app should open for a given entity type.
 * This is the single source of truth for entity -> app mappings.
 */

import { entityTypeRegistry } from '../services/entityTypeRegistry';
import { isGenericTemplateEntityType } from '../apps/TemplateEntityKit/registry/templateEntityTypes.m';

/**
 * Determine the appropriate app type for an entity
 * Handles both legacy types (PDF, Doc, Note) and hierarchical types (File.Document.PDF, etc.)
 */
export function getAppTypeForEntity(entity: any): string {
  const entityType = entity.entityType || entity.type;

  // Unified email model: a Mail.Thread (draft, sent, or synced conversation)
  // opens as EmailEntityView — state-driven composer vs read view — never the
  // tri-pane MailApp. Checked before the registry/legacy resolution so a
  // draft→sent transition does not flip the window into mail-app on reload.
  if (entityType === 'Mail.Thread') {
    return 'entity-view';
  }

  if (entityTypeRegistry.isInitialized()) {
    try {
      const appType = entityTypeRegistry.getRegisteredAppType(entityType);
      if (appType !== null) {
        return appType;
      }
    } catch {
      // Registry lookup failed — fall through to legacy resolution
    }
  }
  
  return getLegacyAppTypeForEntity(entity);
}

/**
 * Legacy app type resolution (fallback)
 * Used until all types are migrated to EntityTypeDefinition
 */
function getLegacyAppTypeForEntity(entity: any): string {
  const entityType = entity.entityType || entity.type;
  const skeleton = entity.skeleton as any;
  
  // AppInstance entities — use the appId stored in skeleton (same value as window.appType)
  if (entityType === 'AppInstance') {
    const appId = skeleton?.appId;
    if (appId) {
      return appId;
    }
  }

  // AppProject and WebsiteProject → App Builder IDE
  if (entityType === 'app_project' || entityType === 'AppProject') {
    return 'app-builder';
  }

  if (entityType === 'website_project' || entityType === 'WebsiteProject') {
    return 'app-builder';
  }
  
  // Generated App entities — use the appId as the appType
  if (entityType === 'App') {
    const appId = skeleton?.appId;
    if (appId) {
      return appId;
    }
  }

  // Manuscript Review Report — dedicated report app
  if (entityType === 'ManuscriptReviewReport') {
    return 'manuscript-review-report';
  }

  // Dashboard — AI-generated structured report with charts and metrics
  // Accept both 'Dashboard' (canonical) and 'dashboard' (lowercase, AI-created)
  if (entityType === 'Dashboard' || entityType === 'dashboard') {
    return 'dashboard';
  }

  // Entity types that use the generic entity viewer
  const specialViewTypes = [
    'NSFWPromptTemplate',
    'ManuscriptReport',
    'ManuscriptDiscoveryReport',
    'CitationVerificationReport',
    'Rule',
    'Window',
    'NewWindow',
    'OpenDialog',
    'SearchResults',
    'ComputeSession',
    // CSV and spreadsheets use CSVTableView (EntityViewApp) for better data grid UX
    'File.Data.CSV',
    'File.Spreadsheet.Excel',
    'File.Spreadsheet.OpenDocument',
  ];
  
  if (specialViewTypes.includes(entityType)) {
    return 'entity-view';
  }
  
  // External integrations
  if (entityType === 'GoogleDriveFile' || entityType?.startsWith('External.GoogleDrive')) {
    return 'google-drive-file';
  }
  
  if (entityType === 'LocalMount') {
    return 'local-file';
  }

  if (entityType === 'Codebase') {
    return 'entity-view';
  }
  
  if (entityType === 'LocalFile' || entityType?.startsWith('External.LocalFile')) {
    return 'local-file';
  }
  
  // Data sources (new)
  if (entityType === 'DataSource' || entityType === 'MarketplaceDataSource') {
    return 'entity-view';
  }

  // Legacy legal/verified sources
  if (entityType === 'VerifiedSource' || entityType === 'LegalCode') {
    return 'legal-code-viewer';
  }
  
  // Doc entities - but check for special file types
  if (entityType === 'Doc') {
    const fileName = skeleton?.fileName || entity.name || '';
    
    if (skeleton?.mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdfium';
    }
    
    return 'docs';
  }
  
  // Canvas entities — always use v2
  if (entityType === 'Canvas') {
    return 'canvas-v2';
  }
  
  // Sheet entities (v2 only)
  if (entityType === 'Sheet' || entityType === 'Spreadsheet') {
    return 'sheets-v2';
  }
  
  // Presentation entities (v2 only)
  if (entityType === 'Presentation' || entityType === 'Slide' || entityType === 'Slides') {
    return 'presentations-v2';
  }
  
  // Page entities (v2 block editor)
  if (entityType === 'Page') {
    return 'page-v2';
  }

  // Transcript entities
  if (entityType === 'Transcript') {
    return 'transcript';
  }

  // ✅ InboxApp — opens the InboxApp view
  if (entityType === 'InboxApp') {
    return 'entity-view';
  }

  // Note entities — always use v2
  if (entityType === 'Note' || entityType === 'Content.Note') {
    return 'notes-v2';
  }

  // Prompt / Memory / Skill / MarkdownDoc entities
  if (entityType === 'Prompt') return 'prompts-v2';
  if (entityType === 'Memory') return 'memory-v2';
  if (entityType === 'Skill') return 'skills-v2';
  if (entityType === 'SkillWizard') return 'skill-wizard';
  if (entityType === 'WizardRun') return 'workflow';
  if (entityType === 'OutboundCampaign') return 'sequences';
  if (entityType === 'SocialPost') return 'socialstudio';
  if (entityType === 'HtmlCanvas') return 'html-canvas';
  if (entityType === 'MarkdownDoc') return 'markdown-v2';
  
  // PDF entities (both legacy and hierarchical)
  if (entityType === 'PDF' || 
      entityType === 'File.Document.PDF' || 
      entityType?.startsWith('File.Document.PDF')) {
    return 'pdfium';
  }
  
  // Image Studio projects
  if (entityType === 'ImageProject') {
    return 'image-studio';
  }

  // Image prompt recipes — open Image Studio so the recipe can be loaded
  if (entityType === 'ImagePromptRecipe') {
    return 'image-studio';
  }

  // VideoStudio timeline projects — Y.Doc-backed timeline editor
  if (entityType === 'VideoProject') {
    return 'videostudio';
  }

  // Diagram — DiagramApp tldraw canvas with Y.Doc record sync
  if (entityType === 'Diagram') {
    return 'diagram';
  }

  // Chart — ChartApp recharts data visualization with Y.Doc spec sync
  if (entityType === 'Chart') {
    return 'chart';
  }

  // Character, Equipment, and every future Generic Template Entity Kit type
  // route to the single SchemaEntityApp. This generic fallback covers them even
  // before their schemas finish registering into entityTypeRegistry.
  if (isGenericTemplateEntityType(entityType)) {
    return 'schema-entity';
  }

  // MedicationTable — MedicationTableApp printable table editor (skeleton.model)
  if (entityType === 'MedicationTable') {
    return 'medtable';
  }

  // Hierarchical File types
  if (entityType?.startsWith('File.Image')) {
    return 'imagestudio';
  }
  
  if (entityType?.startsWith('File.Data')) {
    return 'file-editor';
  }
  
  if (entityType?.startsWith('File.Code')) {
    return 'file-editor';
  }
  
  if (entityType?.startsWith('File.Audio')) {
    return 'file-viewer'; // Shows unsupported message inside FileEditorApp
  }
  
  if (entityType?.startsWith('File.Video')) {
    return 'imagestudio';
  }
  
  if (entityType?.startsWith('File.Archive')) {
    return 'file-viewer';
  }
  
  if (entityType?.startsWith('File.Spreadsheet')) {
    return 'sheets-v2';
  }
  
  if (entityType?.startsWith('File.Presentation')) {
    return 'presentations-v2';
  }
  
  if (entityType?.startsWith('File.Document')) {
    console.log(
      `[entityTypeMapping] ✅ File.Document matched, routing to file-editor: ${JSON.stringify({
        entityType,
        fileName: skeleton?.fileName || entity.name,
      })}`,
    );
    return 'file-editor';
  }
  
  // Generic File type - check by filename extension as last resort
  if (entityType === 'File' || entityType?.startsWith('File.')) {
    const fileName = skeleton?.fileName || entity.name || '';
    
    if (skeleton?.mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdfium';
    }
    
    if (fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
      return 'imagestudio';
    }
    
    if (fileName.toLowerCase().match(/\.(py|js|ts|tsx|jsx|java|cpp|c|h|cs|go|rs|php|rb|swift|html|css|scss)$/)) {
      console.log('[entityTypeMapping] Code file detected by extension, routing to file-editor');
      return 'file-editor';
    }
    
    if (fileName.toLowerCase().match(/\.(json|xml|yaml|yml|csv)$/)) {
      console.log('[entityTypeMapping] Data file detected by extension, routing to file-editor');
      return 'file-editor';
    }
    
    if (fileName.toLowerCase().match(/\.(txt|md|mmd)$/)) {
      return 'file-editor';
    }
    
    // For any other file type, use the readonly viewer. It handles unknown content
    // gracefully (TextViewer fallback, error state, download button) rather than
    // routing to webdocument which expects a specific backend document entity.
    return 'file-viewer';
  }
  
  // SpaceTemplate entities — open StoreApp and navigate to the template detail
  if (entityType === 'SpaceTemplate') {
    return 'store';
  }

  // Contact entities — ContactsApp navigated to that contact
  if (entityType === 'Contact') {
    return 'contacts';
  }

  // TasksApp entity types
  if (entityType === 'Project' || entityType === 'Area') {
    return 'tasks';
  }

  // Goal entities — GoalsApp navigated to that goal. See APP_ARCHITECTURE_GUIDE_V3.md §10
  if (entityType === 'Goal') {
    return 'goals';
  }

  // Task entities — TasksApp navigated to and expanded on that task. See APP_ARCHITECTURE_GUIDE_V3.md §10
  if (entityType === 'Task') {
    return 'tasks';
  }
  
  // Database connection and saved-query entities → DatabaseApp
  if (entityType === 'Database.Connection' || entityType === 'Database.SavedQuery') {
    return 'database';
  }

  // API Connection and MCP Connection entities → unified IntegrationsApp
  if (entityType === 'APIConnection') {
    return 'integrations';
  }

  if (entityType === 'MCPConnection') {
    return 'integrations';
  }
  
  // Crisp conversation — opens CrispConversationEntityView
  if (entityType === 'Crisp.Conversation') {
    return 'crisp-conversation-view';
  }

  // Integration entities (Gmail, Calendar, Analytics, Drive, Ads, Plaid, etc.)
  if (entityType === 'Integration' || entityType?.startsWith('Integration.')) {
    return 'entity-view';
  }
  
  // Automation entities (AutomationsApp) — also matches legacy 'Agent' and orchestrator entities
  if (entityType === 'Automation' || entityType === 'Agent' || entityType === 'OrchestratorAgent') {
    return 'automations';
  }

  if (entityType === 'WebDocument') {
    return 'webdocument';
  }
  
  if (entityType === 'BrowserWindow' || entityType === 'BrowserPage') {
    return 'browser';
  }
  
  if (entityType === 'Chat') {
    return 'chat';
  }
  
  if (entityType === 'Space') {
    return 'entityBrowser';
  }
  
  // Group entities open in EntityViewApp → GroupView (List / Gallery / Workspace / Browser views).
  // Created via shift-click window selection + Cmd+G (gui/src/components/Space/utils/windowGrouping.ts)
  // or the entity sidebar's own instant-group flow — see docs/Interface/Space UI/WINDOW_GROUPING.md.
  if (entityType === 'Group') {
    return 'entity-view';
  }
  
  // Integration.Plaid and BankAccount open inside BankApp
  if (entityType === 'Integration.Plaid' || entityType === 'BankAccount') {
    return 'bank';
  }

  // Legacy Mail.Draft + Mail.Thread → EmailEntityView (see early return above for Thread).
  if (entityType === 'Mail.Draft') {
    return 'entity-view';
  }

  // Mail.Message and Mail.SmartFolder open inside the full MailApp.
  // Mail.SmartFolder routing calls store.selectFolder(entity._id) to show the folder view.
  if (entityType === 'Mail.Message' || entityType === 'Mail.SmartFolder') {
    return 'mail-app';
  }

  if (entityType === 'Messaging.Conversation') {
    return 'messaging-app';
  }

  // Other mail entity types still use the generic entity viewer
  if (entityType === 'Mail.Contact' ||
      entityType === 'Mail.Audience' ||
      entityType === 'Mail.Broadcast') {
    return 'entity-view';
  }
  
  // Writers Dashboard
  if (entityType === 'WritersDashboard') {
    return 'entity-view';
  }

  // Book formatter
  if (entityType === 'BookProject') {
    return 'book_formatter_v2';
  }

  // Entity Graph extracted types
  if (
    entityType === 'Property' || entityType === 'Company' ||
    entityType === 'Deal' || entityType === 'LegalEntity' ||
    entityType === 'LegalFiling' || entityType === 'SecurityInstrument' ||
    entityType === 'Transaction' || entityType === 'Insurance' ||
    entityType === 'Subscription' || entityType === 'ExtractionRun'
  ) {
    return 'entity-graph';
  }

  // Social media imports
  if (entityType === 'Social.Tweet') {
    return 'entity-view';
  }

  // Twitter archive integration record
  if (entityType === 'Integration.TwitterArchive') {
    return 'entity-view';
  }

  // Browser extension entities
  if (entityType === 'Browser.Visit') {
    return 'browser';
  }
  if (entityType === 'Content.WebDocument' || entityType === 'WebDocument') {
    return 'webdocument';
  }

  // Default fallback
  return 'entity-view';
}
